<template>
	<main>
		<TopBottomLayout>
			<ResponsiveLayout>
				<section v-if="topGainingDetailData" class="section_title">
					<div class="title_img">
						<img :src="topGainingDetailData.sector_theme_detail.image_path" loading="lazy" alt="타이틀이미지" />
					</div>
					<div class="title_txt">
						<h2 class="c_title">{{ topGainingDetailData.sector_theme_detail.rise_rate_category_name }}</h2>
						<p :class="Number(topGainingDetailData.sector_theme_detail.rise_rate_weekly_rate) > 0 ? 'plus' : 'minus'">
							{{ formatWithSign(topGainingDetailData.sector_theme_detail.rise_rate_weekly_rate ?? '') }}%
						</p>
					</div>
				</section>
			</ResponsiveLayout>
			<ResponsiveLayout>
				<section v-if="topGainingDetailData" class="section_intro">
					<h3 class="c_title">{{ `${$route.params.sort} 소개` }}</h3>
					<div
						v-dompurify-html="topGainingDetailData.sector_theme_detail.rise_rate_introduction"
						class="c_editorContent c_editorContent_outline"
					></div>
				</section>
			</ResponsiveLayout>
			<ResponsiveLayout>
				<section v-if="topGainingDetailData" class="section_top5">
					<h3 class="c_title">{{ `${$route.params.sort} 내 종목 비중 Top5` }}</h3>
					<div class="top5_content">
						<div v-for="item in topGainingDetailData.event_ratio" :key="item.event_name" class="content_item">
							<h4 class="item_title">{{ item.event_name }}</h4>
							<div class="item_list">
								<div class="progressive_bar">
									<div
										class="bar_background"
										:style="{ width: `${displayStore.isSmallScreen ? windowWidth * 0.775 : 260}px` }"
									></div>
									<div
										class="bar_foreground"
										:style="{
											'--target-width': `${(item.cap_percentage / 100) * (displayStore.isSmallScreen ? windowWidth * 0.775 : 260)}px`,
										}"
									></div>
								</div>
								<span class="percentage">{{ `${item.cap_percentage}%` }}</span>
							</div>
						</div>
					</div>
				</section>
			</ResponsiveLayout>
			<section v-if="topGainingDetailData" class="section_enterprise">
				<ResponsiveLayout>
					<h3 class="c_title enterprise_title">{{ `${$route.params.sort} 내 종목` }}</h3>
				</ResponsiveLayout>
				<StickyScrollTabBar
					:items="Object.keys(topGainingDetailData.events) ?? []"
					:selected-scroll-tab="currentMenuByScroll"
					@update:category="movetToContent"
				/>
				<ResponsiveLayout>
					<div class="enterprise_list">
						<div
							v-for="(item, idx) in topGainingDetailData.events"
							:id="String(idx)"
							:key="idx"
							v-intersection-observer="[onIntersectionObserver, { threshold: 1 }]"
							:value="replaceSpecialCharsWithUnderbar(String(idx))"
							class="list_item"
						>
							<h4 :key="idx" class="list_item_title">
								{{ idx }}
							</h4>
							<div class="list_layout">
								<ShortenBookmarkList
									v-for="(d, i) in item.slice(0, moreNumbers[idx])"
									:key="i"
									:data="d"
									:index="i"
									indexed
									@update:book-mark-refetch="onClickRefetch"
								/>
							</div>
							<MoreButton v-if="moreNumbers[idx] < item.length" @click="getMoreItem(idx as string)" />
						</div>
					</div>
				</ResponsiveLayout>
			</section>
		</TopBottomLayout>
	</main>
</template>

<script setup lang="ts">
// core
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { vIntersectionObserver } from '@vueuse/components';

// api
import { useGetRiseSectorThemeDetailApi } from '@/api/topGaining';
import MoreButton from '@/components/atom/button/MoreButton.vue';
import StickyScrollTabBar from '@/components/atom/tabBar/StickyScrollTabBar.vue';
import ResponsiveLayout from '@/components/layout/main/ResponsiveLayout.vue';
// component
import TopBottomLayout from '@/components/layout/main/TopBottomLayout.vue';
import ShortenBookmarkList from '@/components/rankList/ShortenBookmarkList.vue';
// store
import { useDisplayStore } from '@/stores/display';
// util
import { formatWithSign, replaceSpecialCharsWithUnderbar } from '@/utils/function/format';

// -----variable-----
const route = useRoute();
const displayStore = useDisplayStore();

const moreNumbers = ref<{ [key: string]: number }>({});
const windowWidth = ref(window.innerWidth);

const currentMenuByScroll = ref('');
const isSelectedCategory = ref(false);

// -----api-----
const { data: topGainingDetailData, refetch: refetchTopGainingDetailData } = useGetRiseSectorThemeDetailApi(
	computed(() => {
		return {
			rise_rate_pk: String(route.params.pk) ?? '',
		};
	})
);
// -----function-----
// 더보기 클릭 시 실행 함수
const getMoreItem = (category: string) => {
	moreNumbers.value[category] += 5;
};
// 개별 탭 선택 시 해당 탭 리스트로 이동하는 함수
const movetToContent = (tab: string) => {
	requestAnimationFrame(() => {
		const target = document.getElementById(tab)?.offsetTop ?? 0;
		const headerHeight = document.getElementById('TopHeader')?.offsetHeight ?? 0;
		const targetScroll = target - headerHeight - 90;
		window.scrollTo({
			top: targetScroll,
		});
	});
	isSelectedCategory.value = true;
};
const onIntersectionObserver = ([entry]: IntersectionObserverEntry[]) => {
	if (entry.isIntersecting) {
		if (!isSelectedCategory.value) {
			currentMenuByScroll.value = entry.target.id;
		} else {
			isSelectedCategory.value = false;
		}
	}
};
// 창 크기 변경 시 windowWidth 업데이트
const handleResize = () => {
	if (!displayStore.isSmallScreen) {
		return;
	}
	windowWidth.value = window.innerWidth;
};
// refetch 함수
const onClickRefetch = async () => {
	await refetchTopGainingDetailData();
};

// -----composition-----
onMounted(async () => {
	if (topGainingDetailData.value) {
		Object.keys(topGainingDetailData.value.events).forEach((category) => {
			moreNumbers.value[category] = 5;
		});
	}
	// 모바일에서 프로그래시브바 넓이 설정하기 위함
	window.addEventListener('resize', handleResize);
});
</script>

<style scoped lang="scss">
.c_title {
	font-size: var(--font-s-x-large);
	font-style: normal;
	font-weight: var(--font-w-bold);
	line-height: normal;
}

.section_title {
	display: flex;
	align-items: center;
	gap: 1.6rem;
	.title_img {
		display: flex;
		justify-content: center;
		width: 8rem;
		height: 8rem;
		border-radius: 10000rem;
		background-color: var(--color-bg-mid);
		img {
			width: 4.4rem;
			height: auto;
		}
	}
	.title_txt {
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
		p {
			font-size: var(--font-s-large);
			font-weight: var(--font-w-bold);
			line-height: 2.5rem;
		}
	}
}
.section_intro {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.section_top5 {
	display: flex;
	flex-direction: column;
	gap: 2rem;
	.top5_content {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		place-items: center;
		gap: 1.6rem;
		@media screen and (max-width: 768px) {
			display: grid;
			grid-template-columns: repeat(1, 5fr);
		}
		.content_item {
			display: flex;
			flex-direction: column;
			gap: 0.4rem;
			.item_title {
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-light);
				line-height: 1.9rem;
			}
			.item_list {
				display: flex;
				align-items: center;
				gap: 0.8rem;
				.progressive_bar {
					position: relative;
					overflow: hidden;
					border-radius: 999px;
					.bar_background {
						position: relative;
						height: 16px;
						border-radius: 999px;
						background-color: #393d45;
					}
					.bar_foreground {
						position: absolute;
						top: 0px;
						min-height: 16px;
						border-radius: 999px;
						background: linear-gradient(90deg, #ff5e00 0%, #ffad29 100%);
						animation: fillBar 3s ease-in-out forwards;
					}
				}
				@keyframes fillBar {
					from {
						width: 0;
						opacity: 0;
					}
					to {
						width: var(--target-width);
						opacity: 1;
					}
				}
				.percentage {
					font-size: var(--font-s-mid);
					font-weight: var(--font-w-semi-bold);
					line-height: normal;
					width: 3.4rem;
				}
			}
		}
	}
}

.section_enterprise {
	.enterprise_title {
		margin-bottom: 1rem;
	}
	.enterprise_list {
		display: flex;
		flex-direction: column;
		gap: 4rem;
		padding-top: 4rem;
		.list_item {
			h4 {
				font-size: var(--font-s-regular);
				font-weight: var(--font-w-semi-bold);
				line-height: 2.1rem;
				margin-bottom: 1rem;
			}
			button {
				margin-top: 1.6rem;
			}
		}
	}
	.btn_more {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		width: 100%;
		height: auto; // c_button의 height unset
		padding: 1.3rem 1.6rem;
	}
}

.list_layout {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	// min-height: 36.5rem;
}

.plus {
	color: var(--color-danger);
}
.minus {
	color: var(--color-secondary-Blue);
}
</style>
